/**
 * The submission email the agent sends to markets, from their own mail client.
 *
 * WHY A FILE AND NOT A SEND BUTTON. The agent's relationship with the
 * underwriter is the thing being protected here. If the portal sent this, it
 * would arrive from a server, replies would land somewhere the agent can't see,
 * and the thread would live in a system nobody opens after the first day. As a
 * draft in their own Outlook it is their email: it comes from them, the reply
 * comes back to them, and everything after the first message is just email —
 * which is exactly how Josh described the workflow.
 *
 * ⚠️ THE `X-Unsent: 1` HEADER IS WHAT MAKES THIS WORK. Without it Outlook opens
 * a .eml as a received message — read-only, with no Send button. With it, the
 * same file opens as a composable draft. It is one line and the whole feature
 * depends on it; do not "tidy" it away.
 */

/** RFC 2045 wants base64 in lines of at most 76 characters. */
function wrap76(b64: string): string {
  return b64.replace(/(.{76})/g, '$1\r\n');
}

/** UTF-8 safe base64 — btoa() alone throws on anything above U+00FF. */
function b64utf8(text: string): string {
  return bytesToBase64(new TextEncoder().encode(text));
}

function bytesToBase64(buf: Uint8Array): string {
  let bin = '';
  // Chunked: String.fromCharCode(...) on a multi-megabyte array blows the stack.
  const CHUNK = 0x8000;
  for (let i = 0; i < buf.length; i += CHUNK) bin += String.fromCharCode(...buf.subarray(i, i + CHUNK));
  return btoa(bin);
}

async function blobToBase64(blob: Blob): Promise<string> {
  return bytesToBase64(new Uint8Array(await blob.arrayBuffer()));
}

/**
 * Encode a header value that may contain non-ASCII (an insured called "Café
 * Ltd" would otherwise arrive as mojibake in the subject line).
 */
function encodeHeader(value: string): string {
  // eslint-disable-next-line no-control-regex
  return /^[\x00-\x7F]*$/.test(value) ? value : `=?UTF-8?B?${b64utf8(value)}?=`;
}

export interface EmailAttachment {
  name: string;
  blob: Blob;
  /** Defaults to the blob's own type, then to a generic binary. */
  mime?: string;
}

export interface MarketEmail {
  to: string[];
  subject: string;
  body: string;
  /**
   * Attached under their own names.
   *
   * ⚠️ SEPARATE FILES, NOT ONE MERGED PDF (Josh, 2026-08-30). Merging was slow
   * enough to look like a crash, and it silently excluded Word and Excel, which
   * cannot go into a PDF at all — those were listed as "sent separately" and
   * then never sent. An underwriter filing "2024 - 2026 UMB Loss Runs
   * - Utica.pdf" is better served than one handed page 47 of a blob.
   */
  attachments?: EmailAttachment[];
}

/** Outlook and Exchange typically refuse much beyond this, base64 included. */
export const MAX_EMAIL_BYTES = 20 * 1024 * 1024;

/** Build the .eml. Returns a blob to hand to a download link. */
export async function buildEml(msg: MarketEmail): Promise<Blob> {
  const boundary = `----aas-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  const head = [
    `To: ${msg.to.join(', ')}`,
    `Subject: ${encodeHeader(msg.subject)}`,
    'X-Unsent: 1', // opens as an editable draft — see the warning above
    'MIME-Version: 1.0',
  ];

  const attachments = msg.attachments ?? [];
  if (!attachments.length) {
    return new Blob(
      [
        [
          ...head,
          'Content-Type: text/plain; charset=utf-8',
          'Content-Transfer-Encoding: base64',
          '',
          wrap76(b64utf8(msg.body)),
        ].join('\r\n'),
      ],
      { type: 'message/rfc822' },
    );
  }

  const parts: string[] = [
    ...head,
    `Content-Type: multipart/mixed; boundary="${boundary}"`,
    '',
    `--${boundary}`,
    'Content-Type: text/plain; charset=utf-8',
    'Content-Transfer-Encoding: base64',
    '',
    wrap76(b64utf8(msg.body)),
    '',
  ];

  for (const att of attachments) {
    const mime = att.mime || att.blob.type || 'application/octet-stream';
    // A quote in a filename would end the header parameter early.
    const safe = att.name.replace(/"/g, "'");
    parts.push(
      `--${boundary}`,
      `Content-Type: ${mime}; name="${safe}"`,
      'Content-Transfer-Encoding: base64',
      `Content-Disposition: attachment; filename="${safe}"`,
      '',
      wrap76(await blobToBase64(att.blob)),
      '',
    );
  }
  parts.push(`--${boundary}--`, '');
  const eml = parts.join('\r\n');

  return new Blob([eml], { type: 'message/rfc822' });
}

/**
 * The mailto: fallback, for anyone whose client doesn't do .eml.
 *
 * ⚠️ mailto CANNOT carry an attachment — that is a limit of the scheme, not
 * something we can work around. Callers must download the package separately
 * and say so, or the underwriter gets a covering note about a PDF that isn't
 * there. Bodies are also capped: several clients silently truncate a long
 * mailto, so this trims and tells the reader where to look instead.
 */
export function mailtoLink(msg: MarketEmail): string {
  const MAX_BODY = 1400;
  const body =
    msg.body.length > MAX_BODY
      ? `${msg.body.slice(0, MAX_BODY).trimEnd()}\n\n[Trimmed — the full summary is in the attached package.]`
      : msg.body;
  const q = new URLSearchParams({ subject: msg.subject, body });
  return `mailto:${encodeURIComponent(msg.to.join(','))}?${q.toString().replace(/\+/g, '%20')}`;
}

/**
 * The draft, in COMM-PASS's own submission-email shape.
 *
 * ⚠️ DELIBERATELY THE SAME LAYOUT AS THE EMAIL AAS SENDS (Josh, 2026-08-30) —
 * executive summary, description of operations, account snapshot, loss history.
 * Underwriters already read that format from Agents Alliance; a member's
 * submission arriving in the same shape is read the same way. A second house
 * style would make member submissions look like a different, lesser channel.
 *
 * The one difference is the signature: this goes out over the member's name,
 * from their mailbox, because they are the one placing it.
 */
export function draftMarketEmail(input: {
  businessName: string;
  state?: string;
  lines: string[];
  className?: string | null;
  executiveSummary?: string;
  descriptionOfOps?: string;
  effectiveDate?: string;
  targetPremium?: string;
  incumbentCarrier?: string;
  physicalAddress?: string;
  yearsInBusiness?: string;
  revenue?: string;
  payroll?: string;
  knownLosses?: string;
  /** Kept for the record even though the body no longer prints it — Outlook signs off. */
  senderEmail?: string;
  attachmentName: string;
  /** Underwriter's name, when we have one — "Hi Monica," beats "Hello,". */
  greetName?: string;
  /** The submission id, printed so a reply can be routed back. */
  reference?: string;
}): { subject: string; body: string } {
  const name = input.businessName || 'New submission';
  const linesTxt = input.lines.length ? input.lines.join(', ') : 'Commercial';
  const subject = `New Commercial Submission — ${name}${input.state ? ` (${input.state})` : ''} (${linesTxt})`;

  const snapshot = (
    [
      ['Named insured', input.businessName],
      ['Location', input.physicalAddress],
      ['Lines requested', linesTxt],
      ['Class', input.className ?? ''],
      ['Years in business', input.yearsInBusiness],
      ['Annual revenue', input.revenue],
      ['Annual payroll', input.payroll],
      ['Incumbent carrier', input.incumbentCarrier],
      ['Effective / need-by', input.effectiveDate],
      ['Target premium', input.targetPremium],
    ] as [string, string | undefined][]
  )
    .map(([k, v]) => `• ${k}: ${String(v ?? '').trim() || '—'}`)
    .join('\n');

  const greet = (input.greetName ?? '').trim().split(/\s+/)[0];
  const summary = (input.executiveSummary ?? '').trim();
  const ops = (input.descriptionOfOps ?? '').trim();
  const losses = (input.knownLosses ?? '').trim();

  const parts = [
    `Hi${greet ? ` ${greet}` : ''},`,
    '',
    'Please find a new commercial submission below for your review and quote consideration. Documents are attached.',
    '',
  ];
  if (summary) parts.push('EXECUTIVE SUMMARY', summary, '');
  parts.push('DESCRIPTION OF OPERATIONS', ops || '(see attached application)', '');
  parts.push('ACCOUNT SNAPSHOT', snapshot, '');
  if (losses) parts.push('LOSS HISTORY', losses, '');
  /**
   * ⚠️ NO SIGNATURE LINE HERE (Josh, 2026-08-30). Outlook inserts the sender's
   * own signature block into the draft, so ending the body with their address
   * printed it twice — a bare email address, a gap, then the real signature.
   * The draft stops at the sign-off and lets their signature finish it.
   */
  parts.push(`Attached: ${input.attachmentName}`, '', 'Thank you — happy to provide anything further.');

  /**
   * ⚠️ THE REFERENCE IS LOAD-BEARING, NOT DECORATION. Replies quote the body,
   * so this line comes back with them — and the mail flow reads the id off it
   * to forward a misdirected reply to the right agent without a human. Removing
   * it turns every carrier system that ignores Reply-To into service work.
   *
   * The submitter's address is spelled out for the same reason a person would:
   * so an underwriter who scrolls to the bottom knows who they are dealing with.
   */
  if (input.senderEmail || input.reference) {
    parts.push('', '—');
    if (input.senderEmail) parts.push(`Submitted by ${input.senderEmail} — please reply to that address.`);
    if (input.reference) parts.push(`AAS reference: ${input.reference}`);
  }

  return { subject, body: parts.join('\n') };
}
